import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from 'wagmi'
import { useIsContractAddress, useDebounce } from '~/hooks'
import { useAppDispatch } from '~/state/hooks'
import {
  useSpotSwapUserConfig,
  useSpotSwapPair,
  useSpotSwapTokens,
} from '~/state/dex/hooks'
import {
  updateSpotSwapUserConfig,
  updateSpotSwapPair,
} from '~/state/dex/actions'
import Modal from '~/components/Modal'
import { RowBetween } from '~/components/Layout'
import PrimaryButton from '~/components/Button/PrimaryButton'
import { isAddress, zeroAddress } from 'viem'
import { WETH } from '~/constants/addresses'
import { Polygon } from '~/model/chain/ethereum'
import { compareAddress, getIsWeth } from '~/utils/token'
import styled from 'styled-components'
import iconDot from '~/assets/images/icon-dot.svg'
import iconCheckmark from '~/assets/images/icon-checkmark.svg'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 12px;
  font-size: 12px;
  font-weight: 400;
`

const StyledDesc = styled.div`
  border-radius: 12px;
  padding: 10px;
  background-color: #f7f8f9;
  overflow-wrap: break-word;
  color: ${({ theme }) => theme.colors.textTitle};
`

const StyledInputWrapper = styled.div`
  margin-top: 30px;
  width: 100%;
`

const StyledLabel = styled.div`
  color: ${({ theme }) => theme.colors.textCaption};
`

const StyledEditBtn = styled.button`
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`

const StyledInput = styled.textarea`
  margin-top: 8px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  border-radius: 8px;
  padding: 12px;
  background: #ffffff;
  color: ${({ theme }) => theme.colors.textStrong};
  font-size: 12px;
  outline: none;
  resize: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textPlaceholder};
  }
`

const StyledInputWarning = styled.p`
  margin-top: 8px;
  margin-bottom: 0;
  color: ${({ theme }) => theme.colors.alertRiskText};
`

const StyledAddressTipsContainer = styled.div`
  margin-top: 18px;

  p {
    margin-bottom: 0;
  }
`

const StyledAddressTip = styled.div<{ highLight: boolean }>`
  display: flex;
  justify-content: space-between;
  color: ${({ theme, highLight }) =>
    highLight ? theme.colors.textTitle : theme.colors.textCaption};

  &:last-child {
    margin-top: 16px;
  }
`

const StyledTipHighlight = styled.span<{ highLight: boolean }>`
  font-weight: ${({ highLight }) => (highLight ? '700' : '400')};
  color: ${({ theme, highLight }) =>
    highLight ? theme.colors.textTitle : theme.colors.textCaption};
`

const StyledIcon = styled.img`
  width: 20px;
  height: 20px;
`

interface Props {
  isOpen: boolean
  handleDismiss: () => void
}

const EditRecipientModal: React.FC<Props> = ({ isOpen, handleDismiss }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { address: account } = useAccount()
  const [inputToken, outputToken] = useSpotSwapPair()
  const inputSymbol = inputToken?.symbol
  const outputTokens = useSpotSwapTokens(outputToken?.chainId)

  const { recipient } = useSpotSwapUserConfig()
  const [newRecipient, setNewRecipient] = useState(recipient)
  const debouncedNewRecipient = useDebounce(newRecipient, 300)

  const [inputRow, setInputRow] = useState(2)

  const isValidInput =
    !!debouncedNewRecipient && isAddress(debouncedNewRecipient)
  const showInputWarning =
    !!debouncedNewRecipient && !isAddress(debouncedNewRecipient)
  const isSavingDisabled =
    !debouncedNewRecipient || !isAddress(debouncedNewRecipient)

  // 通过 Across 跨链交易 ETH/WETH 时会有特殊处理，详见 https://docs.across.to/introduction/technical-faq#what-is-the-behavior-of-eth-weth-in-transfers
  // 收款地址是 EOA 钱包时收到的会是 ETH，合约钱包时收到的会是 WETH
  // polygon 的 native token 是 POL，不管是合约钱包还是 EOA 钱包收到的都会是 WETH
  const isSellETH =
    inputSymbol === 'ETH' && compareAddress(inputToken?.address, zeroAddress)
  const isSellWETH =
    inputSymbol === 'WETH' &&
    compareAddress(inputToken?.address, WETH[inputToken?.chainId])
  const isSellETHOrWETH = isSellETH || isSellWETH
  const isOutput2Polygon = outputToken?.chainId === Polygon.chainId
  const needShowTips = isSellETHOrWETH && !isOutput2Polygon

  const isContractAddress = useIsContractAddress(
    debouncedNewRecipient,
    outputToken?.chainId,
  )

  const highlightContractAddress = isValidInput && isContractAddress
  const highlightNormalAddress = isValidInput && !isContractAddress

  const handleInput = (value: string) => {
    // 超过以太坊地址长度 42 个字符后不允许输入
    if (value.length > 42) {
      return
    }

    if (value.length > 38) {
      setInputRow(2)
    } else {
      setInputRow(1)
    }
    setNewRecipient(value)
  }

  const handleUseCurrentAddress = () => {
    setInputRow(2)
    setNewRecipient(account)
  }

  const handleSave = () => {
    if (isSellETHOrWETH) {
      let newOutputToken = outputToken
      const currentOutputIsWeth = getIsWeth(
        outputToken?.address,
        outputToken?.chainId,
      )
      const needReset2Weth =
        (isContractAddress && !currentOutputIsWeth) ||
        (!isContractAddress && isOutput2Polygon && !currentOutputIsWeth)
      const needReset2Eth =
        !isContractAddress &&
        currentOutputIsWeth &&
        !(isOutput2Polygon && currentOutputIsWeth)
      const needReset = needReset2Weth || needReset2Eth
      if (needReset) {
        const tokens = Object.values(outputTokens)
        if (needReset2Weth) {
          newOutputToken = tokens.find((token) =>
            compareAddress(token.address, WETH[outputToken?.chainId]),
          )
        } else {
          newOutputToken = tokens.find((token) =>
            compareAddress(token.address, zeroAddress),
          )
        }
        dispatch(
          updateSpotSwapPair({
            inputToken,
            outputToken: newOutputToken,
          }),
        )
      }
    }

    // 没有变化就不触发 dispatch，避免导致表单清空数量
    if (recipient !== debouncedNewRecipient) {
      dispatch(updateSpotSwapUserConfig({ recipient: debouncedNewRecipient }))
    }
    handleDismiss()
  }

  return (
    <Modal
      title={t('edit_recipient_modal_title')}
      isOpen={isOpen}
      onDismiss={handleDismiss}
      animate={false}
      headerClassName="editRecipientHeader"
      contentStyle={{ maxWidth: '343px' }}
    >
      <StyledContainer>
        <StyledDesc>{t('edit_recipient_modal_desc')}</StyledDesc>
        <StyledInputWrapper>
          <RowBetween>
            <StyledLabel>{t('recipient_address')}</StyledLabel>
            <StyledEditBtn onClick={handleUseCurrentAddress}>
              {t('edit_recipient_modal_use_current_address')}
            </StyledEditBtn>
          </RowBetween>
          <StyledInput
            value={newRecipient}
            onChange={(e) => handleInput(e.target.value)}
            placeholder={t('edit_recipient_modal_input_placeholder')}
            rows={inputRow}
          />
          {showInputWarning && (
            <StyledInputWarning>
              {t('edit_recipient_modal_input_invalid_warning')}
            </StyledInputWarning>
          )}
        </StyledInputWrapper>
        {needShowTips && (
          <StyledAddressTipsContainer>
            <StyledAddressTip highLight={highlightContractAddress}>
              <StyledIcon
                src={highlightContractAddress ? iconCheckmark : iconDot}
                alt="icon"
              />
              <div>
                <p>{t('edit_recipient_modal_contract_address')}</p>
                <p>
                  {t('edit_recipient_modal_contract_address_desc', {
                    inputSymbol,
                  })}
                  <StyledTipHighlight highLight={highlightContractAddress}>
                    WETH
                  </StyledTipHighlight>
                </p>
              </div>
            </StyledAddressTip>
            <StyledAddressTip highLight={highlightNormalAddress}>
              <StyledIcon
                src={highlightNormalAddress ? iconCheckmark : iconDot}
                alt="icon"
              />
              <div>
                <p>{t('edit_recipient_modal_normal_address')}</p>
                <p>
                  {t('edit_recipient_modal_normal_address_desc', {
                    inputSymbol,
                  })}
                  <StyledTipHighlight highLight={highlightNormalAddress}>
                    ETH
                  </StyledTipHighlight>
                </p>
              </div>
            </StyledAddressTip>
          </StyledAddressTipsContainer>
        )}
        <PrimaryButton
          text={t('edit_recipient_modal_button')}
          disabled={isSavingDisabled}
          handleClick={handleSave}
          style={{ marginTop: '20px' }}
        />
      </StyledContainer>
    </Modal>
  )
}

export default EditRecipientModal
